import { useEffect, useRef, useState } from 'react';
import { BRACKET_OPEN_DATE, BRACKET_DEADLINE } from '@/types/bracket';
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from '@/integrations/supabase/client';

export type CountdownState = 'pre-bracket' | 'bracket-open' | 'locked';

export interface CountdownUnits {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

// ── useServerTime ─────────────────────────────────────────────────────────────

/**
 * Fetches the server time once on mount by reading the `Date` response header
 * from the Supabase REST endpoint. Calculates an offset (serverTime - clientTime)
 * and returns a `now()` helper that applies the offset for accurate countdowns.
 * If the fetch fails, offset defaults to 0 (graceful degradation).
 */
export function useServerTime(): { now: () => number; ready: boolean } {
  const offsetRef = useRef<number>(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchOffset() {
      try {
        const clientBefore = Date.now();
        const response = await fetch(`${SUPABASE_URL}/rest/v1/`, {
          headers: {
            apikey: SUPABASE_PUBLISHABLE_KEY,
            Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
          },
        });
        const clientAfter = Date.now();
        const dateHeader = response.headers.get('Date');

        if (dateHeader) {
          const serverTime = new Date(dateHeader).getTime();
          // Use midpoint to reduce latency error
          const clientMid = Math.round((clientBefore + clientAfter) / 2);
          offsetRef.current = serverTime - clientMid;
        }
      } catch {
        // Graceful degradation: offset stays 0
      } finally {
        if (!cancelled) setReady(true);
      }
    }

    fetchOffset();
    return () => { cancelled = true; };
  }, []);

  return {
    now: () => Date.now() + offsetRef.current,
    ready,
  };
}

// ── getCountdownState ─────────────────────────────────────────────────────────

/**
 * Determines the current bracket phase based on server-adjusted time.
 */
export function getCountdownState(serverNow: number): CountdownState {
  const openTime = new Date(BRACKET_OPEN_DATE).getTime();
  const deadlineTime = new Date(BRACKET_DEADLINE).getTime();

  if (serverNow >= deadlineTime) return 'locked';
  if (serverNow >= openTime) return 'bracket-open';
  return 'pre-bracket';
}

// ── formatCountdownUnits ──────────────────────────────────────────────────────

/**
 * Converts milliseconds remaining into days / hours / minutes / seconds.
 */
export function formatCountdownUnits(ms: number): CountdownUnits {
  if (ms <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };

  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds };
}

// ── getTargetDateForState ─────────────────────────────────────────────────────

/**
 * Returns the ISO date string to count down toward for a given state.
 * Returns null when in the 'locked' state (nothing to count down to).
 */
export function getTargetDateForState(state: CountdownState): string | null {
  switch (state) {
    case 'pre-bracket':
      return BRACKET_OPEN_DATE;
    case 'bracket-open':
      return BRACKET_DEADLINE;
    case 'locked':
      return null;
  }
}
